import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isElement, isEmpty } from 'lodash/fp';
import classnames from 'classnames/bind';
import { useToggleState } from 'app/lib/hooks';
import { Icon, IconSize } from 'app/components/core/Icon';
import * as styles from './DialogWithChapters.css';

const cx = classnames.bind(styles);

function focusFirstMatchingField(formId, fieldName) {
    const element = document.getElementById(formId)?.querySelector(`[name^="${fieldName}"]`);

    // Form fields could be children of a "Chapter", so, we need to make sure it's opened
    // before focusing the first available field.
    const details = element.closest('details');

    if (isElement(details)) {
        details.open = true;
    }

    element?.focus();
}

function SummaryItem({ onClick, name, valid, required, label }) {
    const handleClick = useCallback(() => {
        onClick(name);
    }, [onClick, name]);

    return (
        <li tabIndex="0" onClick={handleClick}>
            {label}
            <div className={cx('status', { valid })}>
                {(!required || valid) && <Icon variant="check-small" size={IconSize.EXTRA_SMALL} />}
            </div>
        </li>
    );
}

export function DialogSummary({ formId, summary, open = true }) {
    const { t } = useTranslation();
    const [isOpen, toggleIsOpen] = useToggleState(open);

    useEffect(() => {
        toggleIsOpen(open);
    }, [toggleIsOpen, open]);

    const handleClick = useCallback(
        (event) => {
            event.preventDefault();
            toggleIsOpen();
        },
        [toggleIsOpen]
    );

    const handleClickItem = useCallback(
        (fieldName) => {
            focusFirstMatchingField(formId, fieldName);
        },
        [formId]
    );

    if (isEmpty(summary)) {
        return null;
    }

    const { label, fields, isValid, totalRequired, totalValid } = summary;

    return (
        <details className={styles.summary} open={isOpen}>
            <summary onClick={handleClick} data-valid={isValid}>
                <hgroup>
                    <h4>{label}</h4>
                    <p>
                        {t('jobs:dialogs.definition.required', {
                            number: totalValid,
                            total: totalRequired
                        })}
                    </p>
                </hgroup>
                <Icon
                    variant={isOpen ? 'chevron-down-big' : 'chevron-up-big'}
                    size={IconSize.EXTRA_SMALL}
                />
            </summary>
            <ul>
                {fields?.map((entry) => {
                    const { key, valid, required, label } = entry;
                    return (
                        <SummaryItem
                            key={key}
                            name={key}
                            onClick={handleClickItem}
                            valid={valid}
                            required={required}
                            label={label}
                        />
                    );
                })}
            </ul>
        </details>
    );
}
